import type { ChatObjectTokenAttrs } from '../projects-composer-editor-draft';
import type { Artifact } from './state';

/**
 * An object a chat reference or a composer token opens. `document` is a SESSION
 * document, as in a ref tag.
 */
export type ObjectPreviewTarget =
  | { readonly kind: 'patient'; readonly id: string }
  | { readonly kind: 'document'; readonly id: string; readonly sessionId: string }
  | { readonly kind: 'letter'; readonly id: string; readonly sessionId: string }
  | { readonly kind: 'task'; readonly id: string; readonly sessionId: string }
  | { readonly kind: 'library_document'; readonly id: string }
  | { readonly kind: 'template'; readonly id: string; readonly formTemplate?: boolean }
  | { readonly kind: 'routine'; readonly id: string };

export const objectPreviewArtifact = (target: ObjectPreviewTarget): Artifact => {
  switch (target.kind) {
    case 'patient':
      return { type: 'patient', patientId: target.id };
    case 'document':
      return { type: 'document', sessionId: target.sessionId, documentId: target.id };
    case 'letter':
      return { type: 'letter', sessionId: target.sessionId, letterId: target.id };
    case 'task':
      // There is no single-task preview; a task opens its session's task list.
      return { type: 'tasks', sessionId: target.sessionId };
    case 'library_document':
      return { type: 'library-document', documentId: target.id };
    case 'template':
      return target.formTemplate
        ? { type: 'form-template', templateId: target.id }
        : { type: 'template-builder', templateId: target.id };
    case 'routine':
      return { type: 'workflow', workflowId: target.id };
  }
};

/** What a composer token opens; `null` when it names nothing the side panel can show. */
export const chatObjectPreviewTarget = ({
  kind,
  id,
  formTemplate,
  sessionId,
}: ChatObjectTokenAttrs): ObjectPreviewTarget | null => {
  // An id-less token (a patient with no profile) names nothing to open.
  if (!id) return null;
  switch (kind) {
    case 'patient':
      return { kind, id };
    case 'library_document':
      return { kind, id };
    case 'template':
      return { kind, id, formTemplate: formTemplate === true };
    case 'task':
      return sessionId ? { kind, id, sessionId } : null;
    case 'session':
    case 'collection':
      // XXX: neither has a Work preview, and a draft should not navigate away.
      return null;
  }
};
